<!-- src/app/settings/accruals/pages/employee-leave-detail/employee-leave-detail.component.html -->

<div class="employee-leave-detail-container">
    <!-- Loading -->
    <div *ngIf="isLoading" class="spinner-container">
        <div class="spinner"></div>
    </div>

    <!-- Error -->
    <div *ngIf="errorMessage && !isLoading" class="error-message">
        {{ errorMessage }}
    </div>

    <ng-container *ngIf="!isLoading && employee">
        <!-- Header -->
        <div class="header">
            <div class="header-left">
                <button class="back-btn" type="button" (click)="goBack()">← Back</button>

                <div class="employee-header-block">
                    <div class="employee-name-row">
                        <h2>{{ pageTitle }}</h2>

                        <div class="header-meta">
                            <span class="meta-pill">#{{ employee.employeeNumber }}</span>
                            <span class="meta-pill" *ngIf="employee.company">{{ employee.company.name }}</span>
                            <span class="meta-pill" *ngIf="employee.department">
                                {{ employee.department.code }} {{ employee.department.name }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="header-actions">
                <button class="btn btn-secondary" (click)="openManualAdjustmentModal()">
                    Manual Adjustment
                </button>
                <button class="btn btn-primary" (click)="openAssignPolicyModal()">
                    Assign Policy
                </button>
            </div>
        </div>

        <!-- Balance Cards -->
        <div class="balances-grid">
            <div class="balance-card vacation">
                <div class="balance-label">Vacation Available</div>
                <div class="balance-value" [ngClass]="getBalanceClass(vacationAvailableHours)">
                    {{ vacationAvailableHours }}
                </div>
                <div class="balance-subtext">Used: {{ vacationUsedHours }}</div>
                <div class="balance-policy" *ngIf="employee.vacationLeavePolicy">
                    Active Policy: {{ employee.vacationLeavePolicy.leavePolicy.name }}
                </div>
            </div>

            <div class="balance-card sick">
                <div class="balance-label">Sick Available</div>
                <div class="balance-value" [ngClass]="getBalanceClass(sickAvailableHours)">
                    {{ sickAvailableHours }}
                </div>
                <div class="balance-subtext">Used: {{ sickUsedHours }}</div>
                <div class="balance-policy" *ngIf="employee.sickLeavePolicy">
                    Active Policy: {{ employee.sickLeavePolicy.leavePolicy.name }}
                </div>
            </div>
        </div>

        <!-- Side by side content -->
        <div class="details-columns">
            <!-- Policy History -->
            <div class="section details-column">
                <div class="section-header">
                    <div class="section-title-wrap">
                        <h3>Policy History</h3>
                        <span class="section-count">{{ activePolicyHistory.length }}</span>
                    </div>
                </div>

                <div class="pagination-bar" *ngIf="policyHistoryTotalPages > 1">
                    <button
                        class="pagination-btn"
                        type="button"
                        (click)="goToPreviousPolicyHistoryPage()"
                        [disabled]="!canGoToPreviousPolicyHistoryPage">
                        Previous
                    </button>

                    <div class="pagination-summary">
                        <span>Page {{ policyHistoryCurrentPage }} of {{ policyHistoryTotalPages }}</span>
                        <span class="page-size-pill">{{ policyHistoryPageSize }} per page</span>
                    </div>

                    <button
                        class="pagination-btn"
                        type="button"
                        (click)="goToNextPolicyHistoryPage()"
                        [disabled]="!canGoToNextPolicyHistoryPage">
                        Next
                    </button>
                </div>

                <div class="history-list" *ngIf="pagedActivePolicyHistory.length > 0; else noHistory">
                    <div
                        *ngFor="let row of pagedActivePolicyHistory; trackBy: trackByHistoryId"
                        class="history-row"
                        [ngClass]="getLeaveTypeClass(getNormalizedLeaveType(row.leavePolicy.leaveType))">
                        <div class="history-top-row">
                            <div class="history-title-group">
                                <div class="history-policy-name">
                                    {{ row.leavePolicy.name }}
                                </div>

                                <div class="badges inline-badges">
                                    <span
                                        class="leave-type-badge"
                                        [ngClass]="getLeaveTypeClass(getNormalizedLeaveType(row.leavePolicy.leaveType))">
                                        {{ getNormalizedLeaveType(row.leavePolicy.leaveType) }}
                                    </span>

                                    <span class="policy-badge">
                                        {{ getPolicyAccrualFrequency(row.leavePolicy) }}
                                    </span>
                                </div>
                            </div>

                            <button type="button" class="remove-policy-btn" (click)="openRemovePolicyConfirm(row)">
                                Remove
                            </button>
                        </div>

                        <div class="history-meta">
                            <span>Start Date: {{ row.startDate }}</span>
                            <span *ngIf="row.nextAccrualDate">Next Accrual: {{ row.nextAccrualDate }}</span>
                            <span>Hours/Year: {{ row.leavePolicy.hoursPerYear }}</span>
                            <span>Max Balance: {{ row.leavePolicy.maxBalance }}</span>
                            <span *ngIf="row.createdAt">Created: {{ row.createdAt | date: 'short' }}</span>
                        </div>
                    </div>
                </div>

                <ng-template #noHistory>
                    <div class="empty-state">
                        No active policy history found for this employee.
                    </div>
                </ng-template>


                <!-- Deleted Policy History -->
                <div class="subsection deleted-history-section">
                    <div class="subsection-header">
                        <div class="subsection-title-wrap">
                            <h4>Deleted Policy History</h4>
                            <span class="subsection-count">{{ deletedPolicyHistory.length }}</span>
                        </div>
                    </div>

                    <div
                        class="history-list deleted-list"
                        *ngIf="deletedPolicyHistory.length > 0; else noDeletedHistory">
                        <div
                            *ngFor="let row of deletedPolicyHistory; trackBy: trackByHistoryId"
                            class="history-row deleted-row"
                            [ngClass]="getLeaveTypeClass(getNormalizedLeaveType(row.leavePolicy.leaveType))">
                            <div class="history-top-row">
                                <div class="history-title-group">
                                    <div class="history-policy-name">
                                        {{ row.leavePolicy.name }}
                                    </div>

                                    <div class="badges inline-badges">
                                        <span
                                            class="leave-type-badge"
                                            [ngClass]="getLeaveTypeClass(getNormalizedLeaveType(row.leavePolicy.leaveType))">
                                            {{ getNormalizedLeaveType(row.leavePolicy.leaveType) }}
                                        </span>

                                        <span class="policy-badge">
                                            {{ getPolicyAccrualFrequency(row.leavePolicy) }}
                                        </span>

                                        <span class="deleted-badge">Deleted</span>
                                    </div>
                                </div>
                            </div>

                            <div class="history-meta">
                                <span>Start Date: {{ row.startDate }}</span>
                                <span *ngIf="row.nextAccrualDate">Next Accrual: {{ row.nextAccrualDate }}</span>
                                <span>Hours/Year: {{ row.leavePolicy.hoursPerYear }}</span>
                                <span>Max Balance: {{ row.leavePolicy.maxBalance }}</span>
                                <span *ngIf="row.deletedAt">Deleted: {{ row.deletedAt | date: 'short' }}</span>
                                <span *ngIf="row.deletedBy">
                                    Deleted By: {{ row.deletedBy.firstName }} {{ row.deletedBy.lastName }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <ng-template #noDeletedHistory>
                        <div class="empty-state subtle">
                            No deleted policy history found.
                        </div>
                    </ng-template>
                </div>
            </div>

            <!-- Transaction History -->
            <div class="section details-column">
                <div class="section-header">
                    <div class="section-title-wrap">
                        <h3>Transaction History</h3>
                        <span class="section-count">{{ employeeTransactions.length }}</span>
                    </div>
                </div>

                <div class="pagination-bar" *ngIf="transactionTotalPages > 1">
                    <button
                        class="pagination-btn"
                        type="button"
                        (click)="goToPreviousTransactionPage()"
                        [disabled]="!canGoToPreviousTransactionPage">
                        Previous
                    </button>

                    <div class="pagination-summary">
                        <span>Page {{ transactionCurrentPage }} of {{ transactionTotalPages }}</span>
                        <span class="page-size-pill">{{ transactionPageSize }} per page</span>
                    </div>

                    <button
                        class="pagination-btn"
                        type="button"
                        (click)="goToNextTransactionPage()"
                        [disabled]="!canGoToNextTransactionPage">
                        Next
                    </button>
                </div>

                <div class="transactions-list" *ngIf="pagedEmployeeTransactions.length > 0; else noTransactions">
                    <div
                        *ngFor="let transaction of pagedEmployeeTransactions; trackBy: trackByTransactionId"
                        class="transaction-row"
                        [ngClass]="getLeaveTypeClass(transaction.leaveType)">
                        <div class="transaction-main">
                            <div class="transaction-top-row">
                                <div class="transaction-top-left">
                                    <div class="transaction-title-group">
                                        <div class="transaction-date">
                                            {{ transaction.effectiveDate }}
                                        </div>

                                        <div class="badges inline-badges">
                                            <span
                                                class="leave-type-badge"
                                                [ngClass]="getLeaveTypeClass(transaction.leaveType)">
                                                {{ transaction.leaveType }}
                                            </span>

                                            <span
                                                class="entry-type-badge"
                                                [ngClass]="getEntryTypeClass(transaction.entryType)">
                                                {{ transaction.entryType }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="transaction-impact-card"
                                    [ngClass]="[getLeaveTypeClass(transaction.leaveType), getHoursClass(transaction.hours)]">
                                    <div class="impact-inline-row">
                                        <div class="impact-label">
                                            {{ transaction.leaveType }} Time {{ transaction.hours >= 0 ? 'Gained' : 'Used' }}
                                        </div>
                                        <div class="impact-value">
                                            {{ transaction.hours > 0 ? '+' : '' }}{{ transaction.hours }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="transaction-meta">
                                <span *ngIf="transaction.referenceId">Reference: {{ transaction.referenceId }}</span>
                                <span *ngIf="transaction.createdAt">Created: {{ transaction.createdAt | date: 'short' }}</span>
                                <span *ngIf="transaction.createdBy">
                                    By: {{ transaction.createdBy.firstName }} {{ transaction.createdBy.lastName }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <ng-template #noTransactions>
                    <div class="empty-state">
                        No transactions found for this employee.
                    </div>
                </ng-template>
            </div>
        </div>
    </ng-container>

    <!-- Manual Adjustment Modal -->
    <div *ngIf="manualAdjustmentModalOpen" class="modal-backdrop" (click)="closeManualAdjustmentModal()">
        <div class="modal" (click)="$event.stopPropagation()">
            <h2>Manual Adjustment</h2>

            <p class="modal-text" *ngIf="employee">
                Update balances for <strong>{{ employee.firstName }} {{ employee.lastName }}</strong>.
            </p>

            <div class="modal-form">
                <label class="form-field">
                    <span>Leave Type</span>
                    <select [(ngModel)]="manualAdjustmentForm.leaveType">
                        <option [ngValue]="null">Select leave type</option>
                        <option [ngValue]="'Vacation'">Vacation</option>
                        <option [ngValue]="'Sick'">Sick</option>
                    </select>
                </label>

                <label class="form-field">
                    <span>Adjustment Type</span>
                    <select [(ngModel)]="manualAdjustmentForm.adjustmentMode">
                        <option value="add">Add Hours</option>
                        <option value="subtract">Subtract Hours</option>
                    </select>
                </label>

                <label class="form-field">
                    <span>Hours</span>
                    <input
                        type="number"
                        min="0"
                        step="0.5"
                        [(ngModel)]="manualAdjustmentForm.hours"
                        placeholder="Enter hours" />
                </label>

                <label class="form-field">
                    <span>Note</span>
                    <textarea
                        rows="3"
                        [(ngModel)]="manualAdjustmentForm.note"
                        placeholder="Enter reason for adjustment"></textarea>
                </label>
            </div>

            <div class="modal-buttons">
                <button class="btn btn-secondary" type="button" (click)="closeManualAdjustmentModal()">
                    Cancel
                </button>
                <button
                    class="btn btn-primary"
                    type="button"
                    (click)="saveManualAdjustment()"
                    [disabled]="isSavingManualAdjustment">
                    {{ isSavingManualAdjustment ? 'Saving...' : 'Save Adjustment' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Assign Policy Modal -->
    <div *ngIf="assignPolicyModalOpen" class="modal-backdrop" (click)="closeAssignPolicyModal()">
        <div class="modal" (click)="$event.stopPropagation()">
            <h2>Assign Policy</h2>

            <p class="modal-text" *ngIf="employee">
                Assign a policy to <strong>{{ employee.firstName }} {{ employee.lastName }}</strong>.
            </p>

            <div class="modal-form">
                <label class="form-field">
                    <span>Leave Type</span>
                    <select [ngModel]="assignPolicyForm.leaveType" (ngModelChange)="onAssignLeaveTypeChange($event)">
                        <option [ngValue]="null">Select leave type</option>
                        <option [ngValue]="'Vacation'">Vacation</option>
                        <option [ngValue]="'Sick'">Sick</option>
                    </select>
                </label>

                <label class="form-field">
                    <span>Policy</span>
                    <select [(ngModel)]="assignPolicyForm.policyId">
                        <option [ngValue]="null">Select policy</option>
                        <option *ngFor="let policy of assignablePolicies" [ngValue]="policy.id">
                            {{ policy.name }}
                        </option>
                    </select>
                </label>

                <label class="form-field">
                    <span>Start Date</span>
                    <input type="date" [(ngModel)]="assignPolicyForm.startDate" />
                </label>
            </div>

            <div class="modal-buttons">
                <button class="btn btn-secondary" type="button" (click)="closeAssignPolicyModal()">
                    Cancel
                </button>
                <button
                    class="btn btn-primary"
                    type="button"
                    (click)="saveAssignedPolicy()"
                    [disabled]="isAssigningPolicy">
                    {{ isAssigningPolicy ? 'Saving...' : 'Assign Policy' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Remove Policy Confirm Modal -->
    <div *ngIf="removePolicyConfirmOpen" class="modal-backdrop" (click)="closeRemovePolicyConfirm()">
        <div class="modal confirm-modal" (click)="$event.stopPropagation()">
            <h2>Remove Policy Assignment</h2>

            <p class="modal-text" *ngIf="selectedPolicyHistoryRowForRemoval">
                Remove
                <strong>{{ selectedPolicyHistoryRowForRemoval.leavePolicy.name }}</strong>
                from
                <strong>
                    {{ selectedPolicyHistoryRowForRemoval.employee.firstName }}
                    {{ selectedPolicyHistoryRowForRemoval.employee.lastName }}
                </strong>
                starting
                <strong>{{ selectedPolicyHistoryRowForRemoval.startDate }}</strong>?
            </p>

            <p class="modal-warning">
                This will delete the selected policy start date record.
            </p>

            <div class="modal-actions confirm-actions">
                <button class="btn btn-secondary" type="button" (click)="closeRemovePolicyConfirm()">
                    Cancel
                </button>
                <button
                    class="btn btn-danger"
                    type="button"
                    (click)="confirmRemovePolicyHistoryRow()"
                    [disabled]="isRemovingPolicy">
                    {{ isRemovingPolicy ? 'Removing...' : 'Remove' }}
                </button>
            </div>
        </div>
    </div>
</div>